import { useId, useState, type FormEvent } from 'react'
import { followupContactMissing } from '@k4ia/shared'
import { useT } from '../lib/i18n'

/** Die Felder, die der Besucher in der Karte selbst ausfüllt. */
export interface ContactData {
  /** Worum es geht — eine Zeile je Anliegen; vorbelegt mit dem, was K4ia verstanden hat. */
  anliegen: string
  name: string
  company: string
  phone: string
  email: string
  preferredTime: string
}

export const LEERER_KONTAKT: ContactData = {
  anliegen: '',
  name: '',
  company: '',
  phone: '',
  email: '',
  preferredTime: '',
}

/**
 * Die Kontaktkarte: Statt Name, Firma, Telefon und Mail in den Chat zu
 * tippen (und von K4ia nacheinander abgefragt zu werden), füllt der Besucher
 * eine kleine Karte aus. Die Felder tragen die Autofill-Namen der Browser
 * (`name`, `organization`, `tel`, `email`), damit das Handy sie mit einem
 * Tippen ausfüllt. Der Knopf bleibt grau, bis das Anliegen und Name und
 * Telefon **oder** Mail da sind — dieselbe Regel wie im Werkzeug
 * (`followupContactMissing`).
 *
 * **Worum es geht, steht oben und ist änderbar.** K4ia bringt es aus dem
 * Gespräch mit (Angebot, Frage zum Preis …); war das zu knapp oder falsch,
 * korrigiert der Besucher es hier, statt dass K4ia nachfragt. Das Feld ist
 * Pflicht: Eine Aufgabe ohne Anliegen kann niemand bearbeiten.
 *
 * `onChange` meldet jede Eingabe nach außen: Die Karte kann aus dem Bild
 * wandern (Sprachmodus, neue Antwort) und wieder auftauchen — dann steht
 * noch drin, was der Besucher getippt hat.
 */
export function ContactCard({
  initial,
  busy = false,
  error = null,
  onChange,
  onSubmit,
  onDecline,
}: {
  initial: ContactData
  busy?: boolean
  error?: string | null
  onChange?: (data: ContactData) => void
  onSubmit: (data: ContactData) => void
  onDecline: () => void
}) {
  const [data, setData] = useState<ContactData>(initial)
  const idPrefix = useId()
  const t = useT()
  const fehlt = followupContactMissing(data)
  const anliegenFehlt = data.anliegen.trim() === ''
  const vollstaendig = fehlt.length === 0 && !anliegenFehlt
  const fehltText = [
    ...(anliegenFehlt ? [t.cardMissingReason] : []),
    ...fehlt.map((f) => (f === 'Name' ? t.cardMissingName : t.cardMissingContact)),
  ].join(` ${t.and} `)

  const aendere = (key: keyof ContactData, value: string) =>
    setData((d) => {
      const neu = { ...d, [key]: value }
      onChange?.(neu)
      return neu
    })

  const feld = (key: keyof ContactData) => ({
    id: `${idPrefix}-${key}`,
    value: data[key],
    onChange: (e: { target: { value: string } }) => aendere(key, e.target.value),
    className:
      'w-full rounded-xl border border-[var(--color-border)] bg-surface px-3 py-2.5 text-base outline-none placeholder:text-text-muted focus:border-brand',
  })

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!vollstaendig || busy) return
    // Die Prüfung des Browsers (E-Mail-Form) läuft durch `required`/`type`
    // schon vor dem Submit; hier kommt nur an, was sie durchgelassen hat.
    onSubmit({
      anliegen: data.anliegen.trim(),
      name: data.name.trim(),
      company: data.company.trim(),
      phone: data.phone.trim(),
      email: data.email.trim(),
      preferredTime: data.preferredTime.trim(),
    })
  }

  return (
    <form
      onSubmit={submit}
      className="mt-3 space-y-3 rounded-2xl bg-surface-2 p-4 text-sm"
      data-testid="contact-card"
      aria-label={t.cardLabel}
    >
      <p className="font-semibold">{t.cardTitle}</p>

      <label className="block">
        <span className="mb-1 block text-xs text-text-muted">{t.cardReason}</span>
        <textarea
          {...feld('anliegen')}
          name="anliegen"
          rows={Math.min(4, Math.max(2, data.anliegen.split('\n').length))}
          autoComplete="off"
          placeholder={t.cardReasonPlaceholder}
          required
        />
      </label>

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs text-text-muted">{t.cardName}</span>
          <input
            {...feld('name')}
            type="text"
            name="name"
            autoComplete="name"
            placeholder={t.cardNamePlaceholder}
            required
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-text-muted">{t.cardCompany}</span>
          <input
            {...feld('company')}
            type="text"
            name="organization"
            autoComplete="organization"
            placeholder={t.cardCompanyPlaceholder}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-text-muted">{t.cardPhone}</span>
          <input
            {...feld('phone')}
            type="tel"
            name="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+49 …"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-text-muted">{t.cardEmail}</span>
          <input
            {...feld('email')}
            type="email"
            name="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t.cardEmailPlaceholder}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs text-text-muted">{t.cardTime}</span>
          <input
            {...feld('preferredTime')}
            type="text"
            name="preferredTime"
            autoComplete="off"
            placeholder={t.cardTimePlaceholder}
          />
        </label>
      </div>

      <p className="text-xs text-text-muted" aria-live="polite">
        {error ? error : vollstaendig ? t.cardReady : `${t.cardMissing} ${fehltText}.`}
      </p>

      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={!vollstaendig || busy}
          className="rounded-full bg-accent px-4 py-2 font-semibold text-on-accent transition disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? t.cardSubmitting : t.cardSubmit}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={busy}
          className="rounded-full px-4 py-2 font-semibold text-text-muted hover:text-text"
        >
          {t.cardDecline}
        </button>
      </div>
    </form>
  )
}

/** Liest aus dem Aufruf des Modells vor, was der Besucher schon genannt hat — und das Anliegen. */
export function kontaktAusAufruf(input: Record<string, unknown>): Partial<ContactData> {
  const text = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v : undefined)
  const teil: Partial<ContactData> = {}
  const anliegen = fragenAus(input).join('\n')
  const name = text(input['name'])
  const company = text(input['company'])
  const phone = text(input['phone'])
  const email = text(input['email'])
  const preferredTime = text(input['preferredTime'])
  if (anliegen) teil.anliegen = anliegen
  if (name) teil.name = name
  if (company) teil.company = company
  if (phone) teil.phone = phone
  if (email) teil.email = email
  if (preferredTime) teil.preferredTime = preferredTime
  return teil
}

/** Die Fragen aus dem Aufruf von create_followup. */
export function fragenAus(input: Record<string, unknown>): string[] {
  const fragen = input['questions']
  return Array.isArray(fragen)
    ? fragen.filter((q): q is string => typeof q === 'string' && q.trim() !== '')
    : []
}

/** Das Anliegen aus der Karte als Fragenliste fürs Werkzeug — eine je Zeile. */
export function anliegenAlsFragen(anliegen: string): string[] {
  return anliegen
    .split('\n')
    .map((z) => z.trim())
    .filter((z) => z !== '')
}
